import { FiMail, FiPhone, FiMapPin, FiCalendar, FiBriefcase } from "react-icons/fi";
import { useRole } from "../../context/RoleContext";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { getMyDirectReports } from "../../services/employeeApi";
import { changePassword, passwordPolicy } from "../../services/authApi";
import { getErrorMessage } from "../../utils/errors";
import useAsync from "../../hooks/useAsync";
import { Card, PageHeader, Avatar, PersonCell, StatusBadge, EmptyState, FormField, Button } from "../../components/common";
import { ROLE_LABELS, VIEW_LABELS } from "../../services/navigation";
import { formatDate } from "../../utils/format";
import { sortAlpha } from "../../utils/alphaSort"; // [NEW] A to Z

const api = { getMyDirectReports, changePassword };

function ChangePasswordCard() {
  const { logout } = useRole();
  const navigate = useNavigate();
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    const rules = passwordPolicy(form.next);
    if (!rules.length || !rules.lowercase || !rules.uppercase || !rules.digit || !rules.special || !rules.noSpaces) { toast.error("Use 8 to 128 characters with uppercase, lowercase, a digit, and a special character; spaces are not allowed."); return; }
    if (form.next !== form.confirm) { toast.error("The two new passwords don't match."); return; }
    setBusy(true);
    try {
      await api.changePassword({ currentPassword: form.current, newPassword: form.next });
      toast.success("Password changed. Please sign in again.");
      logout();
      navigate("/login", { replace: true });
    } catch (err) {
      toast.error(getErrorMessage(err));
      setBusy(false);
    }
  };
  return (
    <Card title="Change password" subtitle="8 to 128 characters with uppercase, lowercase, a digit, and a special character; no spaces. You will be signed out on every device.">
      <form onSubmit={(e) => { e.preventDefault(); if (!busy && form.current && form.next) submit(); }}>
      <div className="form-grid">
        <FormField label="Current password" className="span-2">
          <input type="password" autoComplete="current-password" value={form.current} onChange={(e) => setForm({ ...form, current: e.target.value })} />
        </FormField>
        <FormField label="New password">
          <input type="password" autoComplete="new-password" value={form.next} onChange={(e) => setForm({ ...form, next: e.target.value })} />
        </FormField>
        <FormField label="Confirm new password">
          <input type="password" autoComplete="new-password" value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} />
        </FormField>
      </div>
      <div className="side-actions"><Button type="submit" loading={busy} disabled={!form.current || !form.next}>Change password</Button></div>
      </form>
    </Card>
  );
}

export default function ProfilePage() {
  const { profile, availableViews } = useRole();
  const reports = useAsync(() => api.getMyDirectReports(), []);

  return (
    <div className="page">
      <PageHeader title="My profile" subtitle="Your details come from the employee master. Contact HR to change them." />
      <div className="grid-1-2">
        <Card>
          <div className="profile-card">
            <Avatar name={profile.name} size={72} />
            <h2>{profile.name}</h2>
            <p className="muted">{profile.designationName}</p>
            <StatusBadge status={profile.status} />
          </div>
          <ul className="icon-list">
            <li><FiBriefcase size={14} aria-hidden="true" /> {profile.id}, {profile.departmentName}</li>
            <li><FiMail size={14} aria-hidden="true" /> {profile.email}</li>
            {profile.phone && <li><FiPhone size={14} aria-hidden="true" /> {profile.phone}</li>}
            {profile.location && <li><FiMapPin size={14} aria-hidden="true" /> {profile.location}</li>}
            <li><FiCalendar size={14} aria-hidden="true" /> Joined {formatDate(profile.dateOfJoining)}</li>
          </ul>
        </Card>
        <div className="stack">
          <Card title="Access">
            <dl className="kv-list">
              <div><dt>Username</dt><dd>{profile.username}</dd></div>
              <div><dt>Application roles</dt><dd>{(profile.roleKeys || [profile.role]).map((r) => ROLE_LABELS[r]).join(", ")}</dd></div>
              <div><dt>Views you can switch to</dt><dd>{availableViews.map((v) => VIEW_LABELS[v]).join(", ")}</dd></div>
            </dl>
          </Card>
          {/* [NEW] HR details from the employee master (backend migration 010). Shown only when HR has filled them. */}
          {[profile.legalEntity, profile.businessUnit, profile.primarySkill, profile.professionalExperience, profile.personalEmail, profile.dateOfBirth, profile.gender, profile.currentAddress].some(Boolean) && (
            <Card title="HR details">
              <dl className="kv-list">
                {profile.legalEntity && <div><dt>Legal entity</dt><dd>{profile.legalEntity}</dd></div>}
                {profile.businessUnit && <div><dt>Business unit</dt><dd>{profile.businessUnit}</dd></div>}
                {profile.primarySkill && <div><dt>Primary skill</dt><dd>{profile.primarySkill}</dd></div>}
                {profile.professionalExperience && <div><dt>Experience</dt><dd>{profile.professionalExperience}</dd></div>}
                {profile.personalEmail && <div><dt>Personal email</dt><dd>{profile.personalEmail}</dd></div>}
                {profile.dateOfBirth && <div><dt>Date of birth</dt><dd>{formatDate(profile.dateOfBirth)}</dd></div>}
                {profile.gender && <div><dt>Gender</dt><dd>{profile.gender}</dd></div>}
                {profile.currentAddress && <div><dt>Current address</dt><dd>{profile.currentAddress}</dd></div>}
              </dl>
            </Card>
          )}
          <Card title="Reporting manager">
            {profile.manager ? <PersonCell name={profile.manager.name} meta={[profile.manager.designationName, profile.manager.id && `Employee ID ${profile.manager.id}`, profile.manager.email].filter(Boolean).join(", ")} size={40} /> : <p className="muted">You're at the top of the hierarchy.</p>}
          </Card>
          <ChangePasswordCard />
          <Card title={`Direct reports (${reports.data?.length ?? 0})`}>
            {reports.data?.length ? (
              <ul className="people-list">
                {sortAlpha(reports.data).map((r) => <li key={r.id}><PersonCell name={r.name} meta={r.designationName} /></li>)}
              </ul>
            ) : <EmptyState compact title="No direct reports" />}
          </Card>
        </div>
      </div>
    </div>
  );
}